import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Flex, Text, Box, Container } from '@radix-ui/themes';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export default function AuthGuard({ children, fallback }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show sophisticated financial loading screen while checking authentication
  if (isLoading) {
    return (
      fallback || (
        <Box
          style={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
            overflow: 'hidden',
          }}
        >
          {/* Financial grid overlay */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `
                linear-gradient(90deg, rgba(0, 255, 127, 0.03) 1px, transparent 1px),
                linear-gradient(rgba(0, 255, 127, 0.03) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }}
          />

          {/* Subtle glow effects */}
          <Box
            style={{
              position: 'absolute',
              top: '20%',
              left: '10%',
              width: '300px',
              height: '300px',
              background:
                'radial-gradient(circle, rgba(0, 255, 127, 0.05) 0%, transparent 70%)',
              borderRadius: '50%',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: '20%',
              right: '10%',
              width: '200px',
              height: '200px',
              background:
                'radial-gradient(circle, rgba(0, 191, 255, 0.03) 0%, transparent 70%)',
              borderRadius: '50%',
            }}
          />

          <Container size="1" style={{ position: 'relative', zIndex: 1 }}>
            <Flex direction="column" align="center" gap="4">
              {/* Financial-themed logo/icon */}
              <Box
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '18px',
                  background:
                    'linear-gradient(135deg, #00ff7f 0%, #00d4aa 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  color: '#0f172a',
                  animation: 'float 3s ease-in-out infinite',
                  boxShadow: '0 20px 40px rgba(0, 255, 127, 0.3)',
                }}
              >
                🔐
              </Box>

              {/* Loading spinner */}
              <Box
                style={{
                  width: '36px',
                  height: '36px',
                  border: '3px solid rgba(0, 255, 127, 0.2)',
                  borderTop: '3px solid #00ff7f',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />

              {/* Loading text */}
              <Flex direction="column" align="center" gap="2">
                <Text
                  size="5"
                  weight="bold"
                  style={{
                    background:
                      'linear-gradient(135deg, #00ff7f 0%, #00d4aa 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  ChainTracks
                </Text>
                <Text size="2" style={{ color: '#94a3b8' }}>
                  Checking authentication...
                </Text>
              </Flex>
            </Flex>
          </Container>

          {/* Add CSS animations */}
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-8px); }
            }
          `}</style>
        </Box>
      )
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
}
